import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { type TemplateRow, deleteTemplate, listTemplates, syncTemplates } from '../api';
import { TemplatePreviewModal } from './TemplatePreviewModal';

const STATUS_LABEL: Record<TemplateRow['status'], { label: string; classes: string }> = {
  approved: { label: 'Aprovado', classes: 'bg-success-50 text-success-700' },
  pending: { label: 'Pendente', classes: 'bg-warning-50 text-warning-700' },
  rejected: { label: 'Rejeitado', classes: 'bg-danger-50 text-danger-700' },
  disabled: { label: 'Desabilitado', classes: 'bg-n-100 text-n-600' },
  paused: { label: 'Pausado', classes: 'bg-warning-50 text-warning-700' },
};

export function TemplatesTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['templates'], queryFn: () => listTemplates() });
  const all = q.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);
  const syncMut = useMutation({
    mutationFn: syncTemplates,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
  // Template aberto no modal de prévia (null = fechado).
  const [previewTarget, setPreviewTarget] = useState<TemplateRow | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template removido da lista', {
        description: '“Sincronizar com Meta” traz de volta.',
      });
    },
    onError: (err) => {
      if (err instanceof ApiError)
        toast.error(err.problem.title, { description: err.problem.detail });
      else toast.error('Falha ao remover template');
    },
  });

  const askDelete = async (t: TemplateRow): Promise<void> => {
    const ok = await confirm({
      title: 'Remover template da lista?',
      description: `"${t.name}" será ocultado do painel. NÃO apaga na Meta — clique "Sincronizar com Meta" pra trazer de volta.`,
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (ok) deleteMut.mutate(t.id);
  };

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Templates HSM
          </h2>
          <p className="text-[13px] text-n-500">
            Templates aprovados pelo Meta — usados pra mensagens fora da janela 24h.
          </p>
        </div>
        <Button onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
          {syncMut.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          Sincronizar com Meta
        </Button>
      </header>

      {syncMut.data && (
        <div className="mb-4 flex-shrink-0 rounded-md bg-success-50 px-4 py-2 text-[12px] text-success-700">
          Sync concluído: {syncMut.data.synced_accounts} contas, {syncMut.data.upserted_templates}{' '}
          templates atualizados.
          {syncMut.data.errors.length > 0 && ` ${syncMut.data.errors.length} erros.`}
        </div>
      )}

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'template' : 'templates'}`
            : `Mostrando ${visible.length} de ${all.length} templates`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Nome
              </th>
              <th className="w-[180px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Canal
              </th>
              <th className="w-[80px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Idioma
              </th>
              <th className="w-[120px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Categoria
              </th>
              <th className="w-[100px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Variáveis
              </th>
              <th className="w-[110px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Status
              </th>
              <th className="w-[160px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Última sync
              </th>
              <th className="w-[90px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-n-500">
                  Nenhum template ainda. Crie no Meta Business Manager e clique "Sincronizar".
                </td>
              </tr>
            )}
            {visible.map((t) => {
              const s = STATUS_LABEL[t.status];
              const channels = t.channelNumbers ?? [];
              return (
                <tr key={t.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                  <td className="px-[14px] py-[11px] font-medium text-n-900">{t.name}</td>
                  <td className="px-[14px] py-[11px] text-n-700">
                    {channels.length === 0 ? (
                      <span className="text-n-400">—</span>
                    ) : (
                      channels.join(', ')
                    )}
                  </td>
                  <td className="px-[14px] py-[11px] text-n-700">{t.language}</td>
                  <td className="px-[14px] py-[11px] text-[12px] text-n-700">{t.category}</td>
                  <td className="px-[14px] py-[11px] text-n-700">{t.variableCount}</td>
                  <td className="px-[14px] py-[11px]">
                    <span
                      className={`rounded-pill px-2 py-0.5 text-[11px] font-semibold ${s.classes}`}
                    >
                      {s.label}
                    </span>
                  </td>
                  <td className="px-[14px] py-[11px] text-[12px] text-n-500">
                    {t.lastSyncedAt ? new Date(t.lastSyncedAt).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setPreviewTarget(t)}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700"
                        title="Ver prévia"
                      >
                        <Eye size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void askDelete(t)}
                        disabled={deleteMut.isPending}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-n-500 hover:bg-danger-50 hover:text-danger-600 disabled:opacity-40"
                        title="Remover da lista"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={8} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 flex-shrink-0 text-[11px] text-n-500">
        ℹ️ Criação de templates é feita direto no{' '}
        <a
          href="https://business.facebook.com/wa/manage/message-templates/"
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-brand-600 underline"
        >
          Meta Business Manager
        </a>
        .
      </p>

      {previewTarget && (
        <TemplatePreviewModal template={previewTarget} onClose={() => setPreviewTarget(null)} />
      )}
    </section>
  );
}
